"use client";

import type { Program } from "@/common/program/types";
import Image from "next/image";
import CTALink from "@/components/CTA/ctaLink";
import { fixUrlSrc } from "@/utils/functions/fixUrlSrc";
import useTranslations from "@/i18n/useTranslations";

interface ThankYouHeaderProps {
  program: Program;
  mainHeading?: string;
  descriptionText?: string;
  applicationUrl?: string;
}

const addOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const getNextCohortStartDate = (program: Program): string | null => {
  if (program.courses && program.courses.length > 0) {
    for (const course of program.courses) {
      if (course.entitlements && course.entitlements.length > 0) {
        for (const entitlement of course.entitlements) {
          if (entitlement.expires) {
            try {
              const expiresDate = new Date(entitlement.expires);
              if (expiresDate > new Date()) {
                const estimatedStartDate = new Date(expiresDate);
                estimatedStartDate.setMonth(estimatedStartDate.getMonth() - 6);

                if (estimatedStartDate > new Date()) {
                  const month = estimatedStartDate.toLocaleString("en-US", {
                    month: "long",
                  });
                  const day = estimatedStartDate.getDate();
                  return `${month} ${addOrdinalSuffix(day)}`;
                }
              }
            } catch (error) {}
          }
        }
      }
    }
  }

  const futureDate = new Date();
  futureDate.setMonth(futureDate.getMonth() + 2);
  const month = futureDate.toLocaleString("en-US", { month: "long" });
  const day = futureDate.getDate();
  return `${month} ${addOrdinalSuffix(day)}`;
};

export default function ThankYouHeader({
  program,
  mainHeading,
  descriptionText,
  applicationUrl,
}: ThankYouHeaderProps) {
  const t = useTranslations("DegreeTY");
  const universityName =
    program.partnerNameOverride ||
    program.partners?.[0]?.name ||
    t("universityFallback");

  const heroOrganizationLogo = fixUrlSrc(
    program.customizations?.contentDegreeDetailPage?.hero?.organizationLogo?.[0]
      ?.fileUrl,
  );
  const universityLogo =
    heroOrganizationLogo ||
    program.partnerLogoOverride ||
    program.partners?.[0]?.logoUrl;

  const nextCohortDate = getNextCohortStartDate(program);

  const label = t("startYourApplication");

  return (
    <div className="w-full bg-white border border-gray-200 rounded-xl px-4 pt-4 pb-6 sm:pt-10 sm:px-10 sm:pb-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-center mb-6">
            <div className="w-fit mb-4 mx-auto lg:mx-0 bg-primary p-4">
              {universityLogo && (
                <Image
                  src={universityLogo}
                  alt={t("universityLogoAlt", { name: universityName })}
                  width={330}
                  height={82}
                  className="mr-4"
                />
              )}
            </div>
          </div>

          <h1 className="text-4xl md:text-4xl font-black text-primary-500 mb-5 leading-tight mt-0 pt-0 ">
            {mainHeading || t("firstStepHeading")}
          </h1>

          <p className="text-lg text-gray-700 mb-5 leading-relaxed">
            {descriptionText && (
              <span dangerouslySetInnerHTML={{ __html: descriptionText }} />
            )}
          </p>

          <div className="mt-8">
            <p className="text-lg font-semibold text-gray-800 mb-0">
              {t("nextCohortStarts")}{" "}
              <span className="text-primary">{nextCohortDate}</span>
            </p>
          </div>
        </div>

        <div className="lg:col-span-1 flex justify-center items-center">
          <div className="flex flex-col rounded-xl p-10 h-fit bg-putty-200 gap-6">
            <h3 className="text-2xl font-bold text-primary-500 my-0 text-center">
              {t("readyToApply")}
            </h3>
            <p className="text-gray-800 mb-0 text-lg">
              {t("readyToApplyBody")}
            </p>
            <CTALink
              label={label}
              url={applicationUrl || ""}
              type="brand"
              size="lg"
              className="w-full"
              html_id="start-app"
              location="hero-international-typ"
              enable_tracking={true}
              name="application-cta"
              icon="launch"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
